/**
 * @fileoverview Common Utility Functions
 * 
 * This file contains utility functions used throughout the Heidrick & Struggles
 * Leader Profile application. It provides functions for:
 * 
 * - Form validation and field filtering
 * - API data updates and error handling
 * - Client branding and asset management
 * - Authentication and session management
 * - UI components like logout modals
 * 
 * These utilities are designed to be reusable across different components
 * to promote code consistency and reduce duplication.
 */

import { Button, ButtonVariant, Modal, ModalSize, IconName, snack, InfoBarType } from '@eightfold.ai/octuple';
import Api from '../Api';
import { validateFields, validationOptions } from '../ValidationSchema';
import React, { useContext, useState } from 'react';
import { globalContext } from '../store';
import { useOktaAuth } from '@okta/okta-react';
import { clientInfo } from '../commons/ClientDynamicInfo';
import { SnackbarProps } from '@eightfold.ai/octuple/lib/components/Snackbar';

/**
 * Recursively trims whitespace from all string values in an object
 * 
 * This utility function processes an object (or array) and trims leading/trailing
 * whitespace from all string values while preserving the structure of nested
 * objects and arrays. Non-string values (numbers, booleans, dates, null, undefined)
 * are passed through unchanged.
 * 
 * @param {any} obj - The object/array/value to process
 * @returns {any} A new object with all string values trimmed
 * 
 * @example
 * trimTextFields({ name: '  John  ', age: 30 }) // { name: 'John', age: 30 }
 * trimTextFields(['  text  ', 123]) // ['text', 123]
 */
export const trimTextFields = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string') return obj.trim();
  if (Array.isArray(obj)) return obj.map(trimTextFields);
  // Preserve non-plain objects (Date, dayjs, Map/Set, File, etc.)
  if (typeof obj === 'object' && Object.prototype.toString.call(obj) !== '[object Object]') return obj;
  if (typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      acc[key] = trimTextFields(obj[key]);
      return acc;
    }, {} as any);
  }
  return obj;
};

/**
 * Form field configurations grouped by section
 * 
 * Contains mappings of all form fields used in the different sections of the
 * demographic survey. These are referenced when filtering fields for display
 * based on client configuration.
 * 
 * @property {Array<string>} roleFields - Fields for the Role section
 * @property {Array<string>} companyFields - Fields for the Company section
 * @property {Array<string>} personalFields - Fields for the Personal section
 */
const allFields: any = {
  'roleFields':
    [
      'demographicSurveyData.jobTitle',
      'demographicSurveyData.jobLevel',
      'demographicSurveyData.hireDate',
      'demographicSurveyData.jobFunction',
      'demographicSurveyData.jobSubFunction',
      'demographicSurveyData.yearsAtJobLevel',
      'demographicSurveyData.yearsInLeadershipRole',
      'demographicSurveyData.directReports',
      'demographicSurveyData.teamSize'
    ],
  'companyFields': [
    'demographicSurveyData.industry',
    'demographicSurveyData.industrySector',
    'demographicSurveyData.orgType',
    'demographicSurveyData.orgStructure',
    'demographicSurveyData.orgSize',
    'demographicSurveyData.orgCycle',
    'demographicSurveyData.orgRevenue',
    'demographicSurveyData.workingEnvironment'
  ],
  'personalFields': [
    'demographicSurveyData.countryOfResidence',
    'demographicSurveyData.age',
    'demographicSurveyData.education',
  ]
};


/**
 * Updates demographic data for a leader profile
 * 
 * Makes an API call to update leader profile data, updates the global context,
 * and navigates to the next page on success. Handles errors including authentication
 * issues and API failures.
 * 
 * @param {Object} params - Parameters object
 * @param {Object} params.payload - Data payload to send to the API
 * @param {string} params.leaderProfileId - ID of the leader profile to update
 * @param {string} params.redirectUrl - URL to navigate to on successful update
 * @param {Function} params.setErrorMsg - Function to set error message state
 * @param {Object} params.newContextData - Updated data for global context
 * @param {Function} params.dispatch - Redux dispatch function
 * @param {Function} params.navigate - React Router navigate function
 * @param {Function} params.setShowLoader - Function to control loading state
 * @returns {Promise} Promise that resolves when the update completes
 */
export const updateDemographicData = ({ payload, leaderProfileId, redirectUrl, setErrorMsg, newContextData, dispatch, navigate, setShowLoader }: any) => {
  setShowLoader(true);
  return Api.updateLeaderProfile(payload, leaderProfileId)
    .then((res: any) => {
      if (res && res.success) {
        dispatch({
          type: "SET_LEADER_DATA",
          payload: newContextData,
        });
        navigate(redirectUrl);
      } else if (res?.data?.code === 401 && res?.data?.message === 'Unauthorized Access') {
        setShowLoader(false);
        checkUnauthorized(res, dispatch);
      } else {
        setShowLoader(false);
      }
    })
    .catch((err: any) => {
      setShowLoader(false);
      checkAuthErr(err, dispatch);
    });
}

/**
 * Retrieves dropdown values from lookup data based on dropdown name
 * 
 * @param {string} name - The name of the dropdown to find
 * @param {Array} lookUpData - Array of dropdown lookup data
 * @returns {Array} Array of dropdown values for the specified dropdown name
 */
export const checkDropDownValues = (name: any, lookUpData: any) => {
  let dropDownData: any = []
  lookUpData?.map((item: any) => {
    if (item.dropDownName === name) {
      dropDownData = item.dropDownValues
    }
  })
  return dropDownData
}

/**
 * Retrieves sub-dropdown values based on parent dropdown selection
 * 
 * Used for cascading dropdowns where the child options depend on the parent selection.
 * 
 * @param {string} selectedValue - The selected value in the parent dropdown
 * @param {string} name - The name of the child dropdown
 * @param {Array} lookUpData - Array of dropdown lookup data
 * @returns {Array} Array of sub-dropdown values for the selected parent value
 */
export const checkSubDropDownValues = (
  selectedValue: any,
  name: any,
  lookUpData: any
) => {
  let dropDownData: any = [];
  lookUpData?.map((item: any) => {
    if (item.dropDownName === name) {
      item.dropDownValues.map((i: any) => {
        if (i.value === selectedValue) {
          dropDownData = i.subList;
        }
      });
    }
  });
  return dropDownData;
};

/**
 * Provides footer link values and their click handlers
 * 
 * Creates an array of footer link objects with URLs and onClick handlers
 * that open links in new tabs.
 * 
 * @returns {Array} Array of footer link objects with url and onClick properties
 */
export const footerValues = () => {
  const originHost = window.location.origin;

  /**
   * Opens a URL in a new tab with security attributes
   * 
   * @param {string} url - URL to open in a new tab
   */
  const onClickFooterItems = (url: any) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return [
    {
      url: "Privacy Notice",
      onClick: () => {
        onClickFooterItems(`https://www.heidrick.com/en/privacy/privacy-notice-english`);
      },
    },
    {
      url: "Terms of Use",
      onClick: () => {
        onClickFooterItems(`${originHost}/assets/pdf/terms-of-use.pdf`);
      },
    },
    {
      url: "Need Help?",
      onClick: () => {
        onClickFooterItems(`https://heidricksupport.zendesk.com/hc/en-us`);
      },
    },
  ];
}

/**
 * Determines the validation status based on error condition
 * 
 * @param {boolean} condition - Whether an error condition exists
 * @returns {string} 'error' if condition is true, 'validating' otherwise
 */
export const chkForError = (condition: any) => {
  if (condition) {
    return 'error';
  } else {
    return 'validating';
  }
}

/**
 * Validates a dynamic form with multiple field types and custom validations
 * 
 * Performs validation on a set of form fields and updates the errors state.
 * Can handle different field types (textField, dropDown, etc.) and custom validation logic.
 * 
 * @param {Array} fieldConfigs - Array of field configuration objects with name, value, and type
 * @param {Object} customValidations - Object with custom validation functions
 * @param {Function} setErrors - State setter function to update errors state
 * @returns {boolean} True if the form has validation errors, false otherwise
 */
export const validateDynamicForm = (fieldConfigs: any, customValidations: {}, setErrors: any) => {
  const errors: any = {};

  // Validate each field using validation schema
  fieldConfigs.forEach(({ name, value, type }: any) => {
    const message = validateFields.validate({ [type]: value || '' }, validationOptions)?.error?.details[0]?.message;
    if (message) {
      errors[`${name}Msg`] = message;
      errors[`${name}Status`] = chkForError(message);
    }
  });

  // Run custom validations if provided
  if (customValidations && Object.keys(customValidations).length > 0) {
    Object.entries(customValidations).forEach(([key, validationFn]: any) => {
      const customMessage = validationFn();
      if (customMessage) {
        errors[key] = customMessage;
        errors[`${key.replace('Msg', 'Status')}`] = chkForError(customMessage);
      }
    });
  }

  // Update errors state and return validation result
  if (Object.keys(errors).length > 0) {
    setErrors(errors);
    return true;
  } else {
    setErrors({});
    return false;
  }
};

/**
 * Filters form fields based on client configuration
 * 
 * Determines which fields should be displayed in the form based on client-specific
 * configuration in the demographic survey data. Used to customize forms for different clients.
 * 
 * @param {string} keyName - The key of the field group to filter ('roleFields', 'companyFields', etc.)
 * @param {Object} globalState - Global application state
 * @returns {Array<string>} Array of field paths that should be displayed
 */
export const filterFields = (keyName: string, globalState: any): string[] => {
  const demographicSurveyData = globalState?.clientData?.demographicSurveyData;

  /**
   * Determines if a field should be shown based on client configuration
   * 
   * @param {any} key - The configuration value for the field
   * @returns {boolean} True if the field should be shown, false otherwise
   */
  const checkShowFields = (key: any) => {
    const checkKey = (key === false) ? false : true;
    return (demographicSurveyData && Object.keys(demographicSurveyData).length > 0) ? checkKey : true
  }

  return allFields[keyName].reduce((acc: string[], field: string) => {
    const key = field && field?.split('.').pop();
    if (globalState?.clientData?.demographicSurveyData) {
      if (key && checkShowFields(globalState?.clientData?.demographicSurveyData?.[key])) {
        acc.push(field);
      }
      return acc;
    } else {
      return allFields[keyName];
    }
  }, []);
}

/**
 * Checks if any demographic field is enabled for the client
 * 
 * Determines whether at least one demographic question is enabled across all
 * onboarding sections (Role, Company, Personal). Used to decide whether to show
 * the demographic acknowledgement modal and whether to skip demographic steps entirely.
 * 
 * @param {Object} clientSurveyData - Client-specific demographic survey configuration
 * @returns {boolean} True if at least one demographic field is enabled, false otherwise
 */
export const hasAnyDemographicFieldEnabled = (clientSurveyData: any): boolean => {
  // If no client survey data exists, assume all fields are enabled (default behavior)
  if (!clientSurveyData || Object.keys(clientSurveyData).length === 0) {
    return true;
  }

  // If client survey data exists, treat the presence of any explicitly enabled flag as "demographics enabled"
  
  return Object.values(clientSurveyData).some((value) => value === true);
}

export const handleDemographicAcknowledge = (dispatch:any, setShowDemographicModal: (value: boolean) => void) => {
  dispatch({ type: 'SET_DEMOGRAPHIC_ACKNOWLEDGED', payload: true });
  return setShowDemographicModal(false);
};

/**
 * Retrieves client-specific information based on the current client name
 * 
 * Gets a specific property value from clientInfo configuration.
 * Falls back to default ('*') client info if the client-specific value doesn't exist.
 * 
 * @param {Object} globalState - Global application state
 * @param {string} key - The property key to retrieve from clientInfo
 * @returns {any} The client-specific value for the requested key
 */
const getClientInfoValue = (globalState: any, key: any) => {
  const clientName = globalState?.clientData?.name;
  if (clientInfo.hasOwnProperty(clientName)) {
    return clientInfo[clientName][key];
  } else {
    return clientInfo['*'][key];
  }
}

/**
 * Gets the client-specific onboarding image path
 * 
 * @param {Object} globalState - Global application state
 * @returns {string} Path to the client-specific left panel image
 */
export const getOnboardingImg = (globalState: any) => {
  return getClientInfoValue(globalState, 'leftImage');
}

/**
 * Gets the client-specific header logo path
 * 
 * @param {Object} globalState - Global application state
 * @returns {string} Path to the client-specific header logo image
 */
export const getHeaderLogo = (globalState: any) => {
  return getClientInfoValue(globalState, 'headerLogo');
}

/**
 * Gets the client-specific header background image path
 * 
 * @param {Object} globalState - Global application state
 * @returns {string} Path to the client-specific header background image
 */
export const getHeaderImg = (globalState: any) => {
  return getClientInfoValue(globalState, 'headerImage');
}

/**
 * Checks if an unauthorized access response was received
 * 
 * Detects if a response contains an unauthorized access error (401)
 * and triggers the logout modal if so.
 * 
 * @param {Object} response - API response object
 * @param {Function} dispatch - Redux dispatch function to update global state
 */
export const checkUnauthorized = (response: any, dispatch: any) => {
  if (response?.data?.code === 401 && response?.data?.message === 'Unauthorized Access') {
    dispatch({
      type: "SET_SHOW_LOGOUT_MODAL",
      payload: true,
    });
  }
}

/**
 * Checks if an error is related to authentication
 * 
 * Detects if an error is related to unauthorized access (401)
 * and triggers the logout modal if so.
 * 
 * @param {Object} err - Error object from API catch block
 * @param {Function} dispatch - Redux dispatch function to update global state
 */
export const checkAuthErr = (err: any, dispatch: any) => {
  if (err?.code === 401 && err?.message === 'Unauthorized Access') {
    dispatch({
      type: "SET_SHOW_LOGOUT_MODAL",
      payload: true,
    });
  }
}

/**
 * Modal Body Component for Session Expiry
 * 
 * Renders the content of the session expiry modal with an explanatory message
 * and a "Continue" button to trigger the logout process.
 * 
 * @param {Object} props - Component props
 * @param {Function} props.closeLogoutModal - Function to close modal and logout
 * @returns {JSX.Element} The modal body content
 */
const ModalBody = (props: any) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ paddingBottom: '20px' }}>
        Your session has expired due to inactivity. Please log in again.
      </div>
      <div style={{ display: 'flex', alignSelf: 'flex-end' }}>
        <Button
          text="Continue"
          variant={ButtonVariant.Secondary}
          classNames="secondaryButton"
          onClick={() => {
            props?.closeLogoutModal();
          }}
        />
      </div>
    </div>
  )
}

/**
 * Session Expiry Modal Component
 * 
 * Displays a modal dialog when a user's session has expired due to inactivity.
 * Uses the global context to access and update session state.
 * Integrates with Okta authentication to handle the logout process.
 * 
 * @returns {JSX.Element} Modal component that notifies users of session expiration
 */
export const ShowLogout = () => {
  const { globalState, dispatch } = useContext(globalContext);
  const { oktaAuth } = useOktaAuth();

  /**
   * Handles the user logout process
   * Clears session storage and signs out of Okta authentication
   * Falls back to page reload if Okta auth is not available
   */
  const logoutUser = () => {
    sessionStorage.clear();
    if (oktaAuth) {
      oktaAuth.signOut({
        postLogoutRedirectUri: window.location.origin
      });
    } else {
      window.location.reload();
    }
  };

  /**
   * Closes the logout modal and performs logout action
   * Updates the global state to hide the modal
   */
  const closeLogoutModal = () => {
    logoutUser();
    dispatch({
      type: "SET_SHOW_LOGOUT_MODAL",
      payload: false,
    });
  }

  return (
    <Modal
      visible={globalState?.showLogoutModal}
      actionsClassNames="my-modal-actions-class"
      body={<ModalBody closeLogoutModal={closeLogoutModal} />}
      bodyClassNames="my-modal-body-class"
      bodyPadding
      configContextProps={{
        noGradientContext: false,
        noThemeContext: false
      }}
      data-test-id="my-modal-test-id"
      header="Session Expired!"
      headerClassNames="my-modal-header-class"
      modalClassNames="my-modal-class"
      modalWrapperClassNames="my-modal-wrapper-class"
      onClose={closeLogoutModal}
      overlay
      positionStrategy="absolute"
      renderContentAlways
      size={ModalSize.small}
      themeContainerId="my-modal-theme-container"
      zIndex={1000}
    />
  )
}

/**
 * Extracts a token from a URL's query parameters
 * 
 * @param {string} url - The URL containing the token query parameter
 * @returns {string|null} The token value or null if not found
 */
export const getToken = (url: any) => {
  const urlParams = new URLSearchParams(new URL(url).search);
  return urlParams.get('token');
};

/**
 * Finds a branding value from a branding object array by key
 * 
 * @param {Array} brandingObject - Array of branding objects with key-value pairs
 * @param {string} brandKey - The key to search for in the branding objects
 * @returns {Object|undefined} The matching branding object or undefined if not found
 */
export const getBrandValues = (brandingObject: any, brandKey: string) => {
  return brandingObject?.find((branding: any) => branding.key === brandKey);
}

/**
 * Fetches and sets brand images from attachments or falls back to default
 * 
 * Downloads image attachments from the server and updates state with the image URL.
 * Falls back to default images from clientInfo if attachment is missing or download fails.
 * 
 * @param {Object} attachmentId - Attachment object containing value property with the attachment ID
 * @param {Function} stateToUpdate - State setter function to update with the image URL
 * @param {string} keyName - Key name to use when falling back to default clientInfo images
 * @returns {Promise<void>} Resolves when the image is retrieved and state is updated
 */

export const getBrandImages = async (attachmentId: any, stateToUpdate: any, keyName: string, dispatch?: any, type?: any) => {
  if (attachmentId != null && attachmentId !== '' && attachmentId !== undefined) {
    await Api.downloadAttachments(attachmentId?.value).then((res: any) => {
      if (res?.success) {
        const logoUrl = res?.response?.data?.data?.s3_url;
        stateToUpdate(logoUrl);
        dispatch({
          type: type,
          payload: logoUrl,
        });
      } else {
        stateToUpdate(clientInfo['*'][keyName]);
         dispatch({
          type: type,
          payload: clientInfo['*'][keyName],
        });
      }
    }).catch(() => {
      stateToUpdate(clientInfo['*'][keyName]);
      dispatch({
        type: type,
        payload: clientInfo['*'][keyName],
      });
    });
  } else {
    stateToUpdate(clientInfo['*'][keyName]);
    dispatch({
      type: type,
      payload: clientInfo['*'][keyName],
    });
  }
}

export const dispatchToGlobalState = (stateToUpdate: any, primColor: string, dispatch: any, type?: any) => {
  stateToUpdate(primColor);
  dispatch({
    type: type,
    payload: primColor,
  });
}

interface TProps {
  type: 'warning' | 'positive' | 'neutral' | 'disruptive';
  content: string;
}

export const showNeutralSnackbar = ({ content }: Pick<TProps, 'content'>) => {
  return showSnackbar({ type: 'neutral', content });
};

export const showSuccessSnackbar = ({ content }: Pick<TProps, 'content'>) => {
  return showSnackbar({ type: 'positive', content });
};

export const showWarningSnackbar = ({ content }: Pick<TProps, 'content'>) => {
  return showSnackbar({ type: 'warning', content });
};

export const showErrorSnackbar = ({ content }: Pick<TProps, 'content'>) => {
  return showSnackbar({ type: 'disruptive', content });
};

const showSnackbar = ({ type, content, ...extraProps }: TProps & Omit<SnackbarProps, 'type'>) => {
  return snack.serve({
    type: type as InfoBarType,
    position: 'bottom-left',
    duration: 3000,
    closable: false,
    icon: IconName.mdiInformation,
    iconClassNames: 'snackbarIcon',
    content,
    ...extraProps,
  });
};

export const ShowStepper = ({ 
  activeIndex, 
  isRoleStepVisible = true, 
  isCompanyStepVisible = true, 
  isPersonalStepVisible = true 
}: { 
  activeIndex: number;
  isRoleStepVisible?: boolean;
  isCompanyStepVisible?: boolean;
  isPersonalStepVisible?: boolean;
}) => {
  
  // Build the list of visible steps dynamically
  const visibleSteps = [
    { name: 'Account', icon: 'account', isVisible: true }, // Account is always visible
    { name: 'Role', icon: 'role', isVisible: isRoleStepVisible },
    { name: 'Company', icon: 'company', isVisible: isCompanyStepVisible },
    { name: 'Personal', icon: 'personal', isVisible: isPersonalStepVisible },
  ].filter(step => step.isVisible);

  const visibleStepCount = visibleSteps.length;
  
  const stepsClassName = visibleStepCount === 2 ? 'steps steps-two-column' : 'steps steps-multi-column';
  
  return (
    <div className={stepsClassName}>
      {visibleSteps.map((step, index) => (
        <div className="step" key={step.name}>
          <img 
            src={(activeIndex === index) ? `./assets/img/${step.icon}-active.svg` : `./assets/img/${step.icon}-in-active.svg`} 
            alt={step.name} 
            className={(activeIndex === index) ? "activeIcon" : "stepIcon"}
          />
          <div className="stepText">{step.name}</div>
        </div>
      ))}
    </div>
  );
}

export const applyGlobalBranding = (globalState: any, setHeaderLogo: any, setHeaderImage: any, setPrimaryColor: any, dispatch: any) => {
  const headerLogoId = getBrandValues(globalState?.clientData?.branding, 'COMPANY_LOGO');
  const headerImgId = getBrandValues(globalState?.clientData?.branding, 'WELCOME_PAGE_HEADER_IMAGE');
  if (globalState?.headerLogo) {
    setHeaderLogo(globalState?.headerLogo);
  } else {
    getBrandImages(headerLogoId, setHeaderLogo, 'headerLogo', dispatch, 'SET_HEADER_LOGO');
  }

  if (globalState?.headerImg) {
    setHeaderImage(globalState?.headerImg);
  } else {
    getBrandImages(headerImgId, setHeaderImage, 'headerImage', dispatch, 'SET_HEADER_IMG');
  }

  if (globalState?.primaryColor) {
    setPrimaryColor(globalState?.primaryColor);
  } else {
    const primColor = getBrandValues(globalState?.clientData?.branding, 'PRIMARY_COLOR')?.value || '#BA3A19';
    dispatchToGlobalState(setPrimaryColor, primColor, dispatch, 'SET_PRIMARY_COLOR');
  }
}

export const applyClientBranding = (setHeaderImage: any, setHeaderLogo: any, title: string) => {
  setHeaderImage(clientInfo[title].headerImage);
  setHeaderLogo(clientInfo[title].headerLogo);
};

const updateProjAssignAssessStatus = (immersiveAssessData: any, setShowLoader: any, dispatch: any, isOtherAssessment?: boolean) => {
  return Api.updateProjectAssignmentAssessmentsStatus(immersiveAssessData?.projectAssignmentsData, immersiveAssessData?.assignmentId)
    .then((res: any) => {
    if (res && res.success) {
      if(isOtherAssessment) {
        return true;
      } else {
        window.open(immersiveAssessData?.immersiveAssessmentUrl, '_self');
      }
    } else if (res?.data?.code === 401 && res?.data?.message === 'Unauthorized Access') {
      setShowLoader(false);
      checkUnauthorized(res, dispatch);
    } else {
      setShowLoader(false);
    }
  })
  .catch((err: any) => {
    setShowLoader(false);
    checkAuthErr(err, dispatch);
  });
}

export const launchAssessmentWithStatusUpdate = (immersiveAssessData: any, setShowLoader: any, dispatch: any, isOtherAssessment?: boolean) => {
  if(immersiveAssessData?.assessmentStatus === 'N'){
    setShowLoader(true);
    return Api.updateAssessmentsStatus(immersiveAssessData?.assessmentsData, immersiveAssessData?.assessmentId)
    .then((res: any) => {
      if (res && res.success) {
        return updateProjAssignAssessStatus(immersiveAssessData, setShowLoader, dispatch, isOtherAssessment);
      } else if (res?.data?.code === 401 && res?.data?.message === 'Unauthorized Access') {
        setShowLoader(false);
        checkUnauthorized(res, dispatch);
      } else {
        setShowLoader(false);
      }
    })
    .catch((err: any) => {
      setShowLoader(false);
      checkAuthErr(err, dispatch);
    }); 
  } else {
    window.open(immersiveAssessData?.immersiveAssessmentUrl, '_self');
  }
}

/**
 * Validates if a URL is safe for navigation
 * 
 * @param {string} urlString - URL to validate
 * @returns {boolean} True if URL is safe, false otherwise
 */
export const isValidNavigationUrl = (urlString: string): boolean => {
  if (!urlString || typeof urlString !== 'string') {
    return false;
  }
  
  try {
    // Just validate that it's a well-formed URL, allow any protocol
    new URL(urlString, window.location.origin);
    return true;
  } catch {
    // If URL parsing fails, reject it
    return false;
  }
};

/**
 * Safely navigates to a URL using an anchor element to avoid Snyk vulnerabilities
 * 
 * @param {string} url - The URL to navigate to
 */
export const safeNavigate = (url: string): void => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

/**
 * Updates project assignment rater status
 * 
 * Makes an API call to update the status of a rater for a project assignment.
 * Handles success and error cases, optionally showing snackbar notifications.
 * 
 * @param {Object} params - Parameters object
 * @param {string} params.assignmentId - ID of the assignment
 * @param {string} params.assessmentStructureId - ID of the assessment structure
 * @param {string} params.status - New status to set (e.g., "In Progress")
 * @param {string} params.raterId - ID of the rater to update
 * @param {Function} params.setLoader - Optional function to control loading state
 * @param {boolean} params.showNotifications - Optional flag to show success/error snackbars (default: false)
 * @param {Function} params.onSuccess - Optional callback function to execute on success
 * @param {Function} params.onError - Optional callback function to execute on error
 * @returns {Promise} Promise that resolves when the update completes
 */
export const updateRaterStatus = ({ 
  assignmentId, 
  assessmentStructureId, 
  status, 
  raterId, 
  setLoader, 
  showNotifications = false,
  onSuccess,
  onError ,
  url,
  isInhouse,
  isHeidrickData,
  navigate
}: any) => {
  const payload = {
    assignmentId,
    assessmentStructureId,
    status,
  };

  if (!raterId) {
    if (setLoader) setLoader(false);
    return showErrorSnackbar({ content: 'Unable to update rater status. Missing rater id.' });
  }

  setLoader && setLoader(true);
  return Api.updateProjectAssignmentRatersStatus(payload, raterId)
    .then((response: any) => {
      if (response?.success) {
        if(isInhouse) {
          navigate(url, {
            state: { raterDetails: { ...isHeidrickData } },
          });
        } else if (isValidNavigationUrl(url)) {
          safeNavigate(url);
        } else {
          console.error('Invalid or unsafe URL:', url);
          if (setLoader) setLoader(false);
          if (showNotifications) {
            showErrorSnackbar({ content: 'Invalid redirect URL.' });
          }
        }
      } else {
        if (setLoader) setLoader(false);
        if (showNotifications) {
          showErrorSnackbar({ content: 'Unable to update rater status.' });
        }
        if (onError) onError(response);
      }
      return response;
    })
    .catch((err: any) => {
      if (setLoader) setLoader(false);
      if (showNotifications) {
        showErrorSnackbar({ content: 'Unable to update rater status.' });
      }
      if (onError) onError(err);
      throw err;
    });
};

export const getLeaderData = (globalState: any, dispatch: any, navigate: any, setLeaderName: any) => {
  let loginToken: any = JSON.parse(sessionStorage.getItem('loginToken') ?? 'false');
  const payload = {
    email: loginToken?.idToken?.claims?.email.toLowerCase(),
    clientId: globalState?.clientData?.id,
  };

  Api.getLeaderProfile(payload)
  .then(response => {
    if (response?.success) {
      if (response && response?.response?.data && response?.response?.data?.data.length > 0) {
        const res = response?.response?.data?.data?.[0];
        dispatch({
          type: 'SET_LEADER_DATA',
          payload: res,
        });
        if (!res?.isProfileCompleted) {
          navigate('/welcome');
        }
        setLeaderName(response?.response?.data?.data[0]?.firstName);
      }
    }
    checkUnauthorized(response, dispatch);
  })
  .catch((err: any) => {
    checkAuthErr(err, dispatch);
  });
};

export const initialiseImmersiveAssessment = (params: any) => {
  const { globalState, dispatch, navigate, setLeaderName, setHeaderImage, setHeaderLogo, setPrimaryColor, title } = params;
  document.documentElement.scrollTo(0, 0);
  if (Object.keys(globalState?.immersiveAssessmentData).length === 0) {
    navigate('/dashboard');
  } else if (Object.keys(globalState?.leaderData).length === 0) {
    getLeaderData(globalState, dispatch, navigate, setLeaderName);
  } else {
    if (!globalState?.leaderData?.isProfileCompleted) {
      navigate('/welcome');
    }
  }

  if (clientInfo.hasOwnProperty(title)) {
    applyClientBranding(setHeaderImage, setHeaderLogo, title);
  } else {
    applyGlobalBranding(globalState, setHeaderLogo, setHeaderImage, setPrimaryColor, dispatch);
  }
}

export const getAssessmentDuration = (assessmentType: any) => {
  const assessmentDurations: any = {
  'Business Impact': '~30 min',
  'HCA Long Form': '~60 min',
  'HCA Short Form': '~45 min',
  'Purpose': '~15 min',
  '360': '~30 min',
  '360-Raters': '~40 min',
  'Leadership Signature': '~15 min',
  'Agility': '~40 min',
  'Saville Wave': '~45 min',
  'META': '~60 min',
  'Impact': '~30 min',
  'CXO': '~40 min',
  };
  return assessmentDurations[assessmentType];
}

export const resolveError = (key: string, errors:any, setErrors:any) => {
    if(!errors[`${key}Status`]) return;
    let dependentFields:any = '';
    if(key === 'countryOfResidence') {
      dependentFields = {
        'ethnicityStatus':false,
        'ethnicityMsg':'',
        'lgbtqStatus':false,
        'lgbtqMsg':'',
        'veteranStatusStatus':false,
        'veteranStatusMsg':'',
        'genderStatus':false,
        'genderMsg':'',
        'ageStatus':false,
        'ageMsg':'',
      }
    } else if (key === 'jobFunction') {
      dependentFields = {
        'jobSubFunctionStatus': false,
        'jobSubFunctionMsg': '',
        'jobSubFuncDescribeStatus': false,
        'jobSubFuncDescribeMsg': '',
      }
    } else if (key === 'industry') {
      dependentFields = {
        'industrySectorStatus': false,
        'industrySectorMsg': '',
        'industrySecDescribeStatus': false,
        'industrySecDescribeMsg': '',
      }
    }

    setErrors((prev: any) => ({
      ...prev,
      [`${key}Status`]: false,
      [`${key}Msg`]: '',
      ...dependentFields
    }));
  }

  /**
 * Type definition for sort information
 */
interface SortInfo {
  field?: string;
  order?: string | null;
}

/**
 * Custom hook for managing table sorting
 * 
 * @param {string} initialField - The initial field to sort by (optional)
 * @param {string | null} initialOrder - The initial sort order: 'ascend', 'descend', or null (optional)
 * @returns {Object} Returns sortInfo state, setSortInfo function, handleTableChange handler, and getSortOrderForColumn helper
 */

  export const useTableSort = (initialField?: string, initialOrder: string | null = 'descend') => {
    const [sortInfo, setSortInfo] = useState<SortInfo>({
      field: initialField,
      order: initialOrder
    });
  
    /**
     * Handles table change events from Octuple Table component
     * Updates sort info based on sorter parameter
     * 
     * @param {any} pagination - Pagination info from table (unused)
     * @param {any} filters - Filter info from table (unused)
     * @param {any} sorter - Sorter info from table (can be object or array)
     */
    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
      // Handle both single sorter and array of sorters
      if (Array.isArray(sorter)) {
        // Find the active sorter (the one with an order)
        const activeSorter = sorter.find((s: any) => s.order);
        setSortInfo({
          field: activeSorter?.field || activeSorter?.columnKey,
          order: activeSorter?.order
        });
      } else {
        // When a new column is sorted, reset all others to null state
        setSortInfo({
          field: sorter?.field || sorter?.columnKey,
          order: sorter?.order
        });
      }
    };
  
    /**
     * Helper function to get sort order for a specific column
     * Returns the sort order if the column matches the current sort field, otherwise null
     * 
     * @param {string} columnField - The field name to check
     * @returns {string | null} The sort order ('ascend' or 'descend') or null
     */
    const getSortOrderForColumn = (columnField: string): string | null => {
      return sortInfo?.field === columnField ? sortInfo?.order || null : null;
    };
  
    return {
      sortInfo,
      setSortInfo,
      handleTableChange,
      getSortOrderForColumn
    };
  };
/**
 * TypeScript interfaces for Rater Count Summary
 */
export interface RaterConfig {
  name: string;
  template: string;
  minimum: number;
  recommended: number;
  maximum: number;
  order: number;
  description: string;
}

export interface RaterData {
  _id: string;
  raterProfileId: string;
  type: string;
  raterEmail: string;
  raterFirstName: string;
  raterLastName: string;
  status: string;
  applicationCreatedTs: string;
  completion: string;
  assessmentId: string;
  emailEventIds?: string[];
}

export interface RaterCountSummary {
  [raterType: string]: {
    filled: number;
    minimum: number;
  };
}

export const formatRaterGroupName = (raterGroupName: string = ''): string =>
  raterGroupName
    .replace(/_/g, ' ')
    .split(' ')
    .map((word) => word ? word.charAt(0).toUpperCase() + word.slice(1) : word)
    .join(' ');

/**
 * Generates a summary of rater counts by type, comparing filled vs minimum requirements
 * 
 * @param ratersConfig - Array of rater configuration objects containing minimum requirements
 * @param ratersData - Array of actual rater data objects
 * @returns Object with rater type as key and filled/minimum counts as values
 * 
 * @example
 * const summary = getRaterCountSummary(ratersConfig, ratersData);
 * // Returns: { Peers: { filled: 2, minimum: 5 }, Manager: { filled: 1, minimum: 5 } }
 */
export const getRaterCountSummary = (
  ratersConfig: RaterConfig[] = [],
  ratersData: RaterData[] = []
): RaterCountSummary => {
  const summary: RaterCountSummary = {};

  // Handle undefined or null inputs
  if (!ratersConfig || !ratersData) {
    return summary;
  }

  // Count filled raters by type (only count raters with complete information)
  const filledRatersCount: { [key: string]: number } = {};
  ratersData.forEach((rater) => {
    // Only count if rater has all required fields filled
    if (
      rater?.type &&
      rater?.raterFirstName &&
      rater?.raterLastName &&
      rater?.raterEmail
    ) {
      filledRatersCount[rater.type] = (filledRatersCount[rater.type] || 0) + 1;
    }
  });

  // Build summary object with filled count and minimum requirement for each type
  ratersConfig.forEach((config) => {
    const raterType = config.name;
    if(filledRatersCount[raterType] < config.minimum || filledRatersCount[raterType] === undefined) {
      summary[raterType] = {
        filled: filledRatersCount[raterType] || 0,
        minimum: config.minimum || 0,
      };
    }
  });

  return summary;
};
